import moment from 'moment-timezone';
import { throttle } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSites } from '../../../../core/actions';
import { getApiStatus, toString } from '../../../../core/utils';
import { selectSiteTimeZone, selectSiteTotalCarCount } from '../../../settings/reducer';
import { selectSitesBasic } from '../../../site-selection/reducer';
import { fetchEmployees } from '../../../employees/actions';
import { selectEmployees } from '../../../employees/reducer';
import {
  completeTaskCompletion,
  revertTaskCompletion,
  addTaskCompletion,
} from '../../../overview/actions';
import {
  selectTaskLocations,
  selectCurrentEmployee,
  selectTaskPrefixes,
  selectCalendarTaskCompletions,
  selectMaintenanceTaskCompletions,
} from '../../reducer';
import {
  getTask,
  getTaskLocations,
  getTaskPrefixes,
  setCurrentEmployee,
  getCalendarTaskCompletions,
  fetchMaintenanceTaskCompletions,
} from '../../actions';
import { selectSite } from '../../../../store/authReducer';
import { TaskCompletion, TaskStatus, Interval, TaskType } from '../../models';
import { TaskListDetail, TaskListItem } from '../../components/TaskListItem';
import { DrawerContainer, Drawer } from '../../../../core/components/Drawer';
import { FilterSection } from '../../../../core/components/FilterSection';
import { ProgressBar } from '../../../../core/components/ProgressBar';
import GenericFilterBar, { FilterItem } from '../../../../core/components/FilterBar';
import Loader from '../../../../core/components/Loader';
import { EmployeeDropdownAndAddButton } from '../../../../core/components/EmployeeDropdownAndAddButton';
import { TaskDetails } from '../../components/TaskDetails';
import { TASK_DRAWER_WIDTH } from '../../types';
import { Calendar, CalendarEvent, CalendarViewTypes } from '../../../../core/components/Calendar';
import {
  FilterConfig,
  FilterPlacement,
  PageFilters,
} from '../../../../core/components/PageFilters';
import {
  getPriorityLabel,
  getCompletionDueDate,
  getCompletionDueMoment,
  getCompletionDoneMoment,
  isCompletionOverdue,
  isOverdueTime,
  getCompletionTimeLabel,
  getCompletionForecastData,
  buildMaintenanceQuery,
} from '../helpers';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { BsCalendar2 } from 'react-icons/bs';
import { BsListCheck } from 'react-icons/bs';

export type TaskList = {
  [key: string]: {
    _id: number;
    _taskId: number;
    _showAction: boolean;
    _resource?: string;
    name: string;
    intervalDescription: string;
    start: string;
    end?: string;
  };
};

// ---------------------------------------------------------------------------
// Date helpers (calendar view)
// ---------------------------------------------------------------------------

const toDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getDateRange = (start: Date, end: Date): Date[] => {
  const s = toDay(start);
  const e = toDay(end);
  const range: Date[] = [];
  const step = s <= e ? 1 : -1;
  const current = new Date(s);
  while (step === 1 ? current <= e : current >= e) {
    range.push(new Date(current));
    current.setDate(current.getDate() + step);
  }
  return range;
};

const formatDay = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDaySuffix = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
};

const formatLongDate = (date: Date) => {
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${day}${getDaySuffix(day)} ${date.getFullYear()}`;
};

const formatRangeLabel = (start: Date, end: Date) => {
  const sd = start.getDate();
  const ed = end.getDate();
  const sm = start.toLocaleDateString('en-US', { month: 'long' });
  const em = end.toLocaleDateString('en-US', { month: 'long' });
  const sy = start.getFullYear();
  const ey = end.getFullYear();
  if (sy === ey && sm === em)
    return `${sm} ${sd}${getDaySuffix(sd)}-${ed}${getDaySuffix(ed)}, ${sy}`;
  if (sy === ey) return `${sm} ${sd}${getDaySuffix(sd)} - ${em} ${ed}${getDaySuffix(ed)}, ${sy}`;
  return `${sm} ${sd}${getDaySuffix(sd)}, ${sy} - ${em} ${ed}${getDaySuffix(ed)}, ${ey}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MaintenanceTasks = (): JSX.Element => {
  const dispatch = useDispatch();
  const intl: any = useIntl();

  // -- view state --
  const [viewTab, setViewTab] = useState<'checklist' | 'calendar' | 'completed'>('checklist');
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<any | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // -- filter state (local, shared across views) --
  const [filterBarValues, setFilterBarValues] = useState<Record<string, any>>({
    nameQuery: '',
    locationFilter: [],
    priorityFilter: [],
    taskTypeFilter: [],
    dueDateStart: '',
    dueDateEnd: '',
    statusFilter: [],
    employeeFilter: [],
    intervalFilter: [],
  });

  // -- calendar-specific state --
  const [selectedDates, setSelectedDates] = useState<Date[]>(() => [toDay(new Date())]);
  const [rangeAnchorDate, setRangeAnchorDate] = useState<Date | null>(() => toDay(new Date()));
  const [visibleDates, setVisibleDates] = useState<Date[]>([]);
  const [collapsedDateGroups, setCollapsedDateGroups] = useState<Record<string, boolean>>({});
  const [defaultCalendarView, setDefaultCalendarView] = useState<CalendarViewTypes>(
    CalendarViewTypes.Monthly
  );

  // -- selectors --
  const primarySite = useSelector(selectSite);
  const taskCompletions = useSelector(selectMaintenanceTaskCompletions);
  const calendarTaskCompletions = useSelector(selectCalendarTaskCompletions);
  const timeZone = useSelector(selectSiteTimeZone);
  const totalCarCount = useSelector(selectSiteTotalCarCount);
  const employees = useSelector(selectEmployees);
  const currentEmployeeId = useSelector(selectCurrentEmployee);
  const locations = useSelector(selectTaskLocations);
  const sites = useSelector(selectSitesBasic);
  const taskPrefixes = useSelector(selectTaskPrefixes);
  const taskLoading = useSelector((state: any) => getApiStatus(state, 'GET_TASK'));
  const completionsLoading = useSelector((state: any) =>
    getApiStatus(state, 'GET_TODAYS_TASK_COMPLETIONS')
  );

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .slice(0, 10);

  const fetchData = () => fetchMaintenanceTaskCompletions(buildMaintenanceQuery('', thirtyDaysAgo));

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchData());
  }, [primarySite]);

  useEffect(() => {
    if (!locations.length) dispatch(getTaskLocations());
  }, [dispatch, locations.length]);

  useEffect(() => {
    if (!sites.length) dispatch(fetchSites());
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (!taskPrefixes.length) dispatch(getTaskPrefixes());
  }, [dispatch, taskPrefixes.length]);

  useEffect(() => {
    if (employees.length > 0 && currentEmployeeId == null) {
      dispatch(setCurrentEmployee(employees[0].id));
    }
  }, [employees, currentEmployeeId]);

  // Fetch calendar-specific date-range completions
  useEffect(() => {
    if (viewTab !== 'calendar' || !visibleDates.length) return;
    const timeout = setTimeout(() => {
      const query =
        visibleDates.length === 1
          ? { ...buildMaintenanceQuery(), date: formatDay(visibleDates[0]) }
          : {
              ...buildMaintenanceQuery('', formatDay(visibleDates[0])),
              untilDate: formatDay(visibleDates[1]),
            };
      dispatch(getCalendarTaskCompletions(query));
    }, 250);
    return () => clearTimeout(timeout);
  }, [visibleDates, viewTab]);

  // Responsive calendar view
  useEffect(() => {
    const handleResize = throttle(() => {
      setDefaultCalendarView(
        window.innerWidth < 1001 ? CalendarViewTypes.BiWeekly : CalendarViewTypes.Monthly
      );
    }, 1000);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ---------------------------------------------------------------------------
  // Filtering (shared across both views)
  // ---------------------------------------------------------------------------

  const matchesFilterBar = (tc: TaskCompletion): boolean => {
    const nameQuery = (filterBarValues.nameQuery || '').trim().toLowerCase();
    if (nameQuery && !(tc.task?.name || '').toLowerCase().includes(nameQuery)) return false;

    const locationIds = (filterBarValues.locationFilter || []).map(String);
    if (
      locationIds.length &&
      tc.task?.locationId != null &&
      !locationIds.includes(String(tc.task.locationId))
    )
      return false;

    const priorities = (filterBarValues.priorityFilter || []).map(Number);
    if (
      priorities.length &&
      tc.task?.priority != null &&
      !priorities.includes(Number(tc.task.priority))
    )
      return false;

    const statuses = (filterBarValues.statusFilter || []).map(String);
    if (
      statuses.length &&
      tc.status != null &&
      !statuses.some((s: string) => String(tc.status) === s)
    )
      return false;

    const employeeIds = (filterBarValues.employeeFilter || []).map(String);
    const tcEmployeeId = tc.employeeId ?? tc.task?.employeeId;
    if (employeeIds.length && tcEmployeeId != null && !employeeIds.includes(String(tcEmployeeId)))
      return false;

    const intervals = (filterBarValues.intervalFilter || []).map(String);
    if (intervals.length && !intervals.includes(String(tc.task?.interval))) return false;

    const dueStart = filterBarValues.dueDateStart;
    const dueEnd = filterBarValues.dueDateEnd;
    if (dueStart || dueEnd) {
      const taskDate = getCompletionDueDate(tc);
      if (dueStart && taskDate < new Date(dueStart)) return false;
      if (dueEnd) {
        const endOfDay = new Date(dueEnd);
        endOfDay.setHours(23, 59, 59, 999);
        if (taskDate > endOfDay) return false;
      }
    }

    return true;
  };

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const sourceCompletions =
    viewTab === 'calendar' && visibleDates.length ? calendarTaskCompletions : taskCompletions;

  const filteredCompletions = useMemo(
    () => sourceCompletions.filter(matchesFilterBar),
    [sourceCompletions, filterBarValues]
  );

  const completionList = filteredCompletions.filter((tc: TaskCompletion) =>
    viewTab !== 'completed' ? tc.status !== 'Completed' : tc.status === 'Completed'
  );
  const completedList = filteredCompletions
    .filter((tc: TaskCompletion) => tc.status === 'Completed')
    .sort(
      (a: TaskCompletion, b: TaskCompletion) =>
        new Date(b.timestamp ?? '').getTime() - new Date(a.timestamp ?? '').getTime()
    );

  // -- Checklist time buckets --
  const today = timeZone ? moment().tz(timeZone).startOf('day') : moment().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  const endOfWeek = today.clone().endOf('week');
  const startOfWeek = today.clone().startOf('week');
  const startOfNextWeek = today.clone().add(1, 'week').startOf('week');
  const endOfNextWeek = today.clone().add(1, 'week').endOf('week');

  const overdueCompletions = completionList.filter((tc: TaskCompletion) => isCompletionOverdue(tc));
  const todayCompletions = completionList.filter((tc: TaskCompletion) => {
    const due = getCompletionDueMoment(tc);
    return due ? due.isSame(today, 'day') : false;
  });
  const tomorrowCompletions = completionList.filter((tc: TaskCompletion) => {
    const due = getCompletionDueMoment(tc);
    return due ? due.isSame(tomorrow, 'day') : false;
  });
  const thisWeekCompletions = completionList.filter((tc: TaskCompletion) => {
    const due = getCompletionDueMoment(tc);
    return due ? due.isAfter(tomorrow, 'day') && due.isSameOrBefore(endOfWeek, 'day') : false;
  });
  const nextWeekCompletions = completionList.filter((tc: TaskCompletion) => {
    const due = getCompletionDueMoment(tc);
    return due
      ? due.isSameOrAfter(startOfNextWeek, 'day') && due.isSameOrBefore(endOfNextWeek, 'day')
      : false;
  });
  const laterCompletions = completionList.filter((tc: TaskCompletion) => {
    const due = getCompletionDueMoment(tc);
    if (!due) return true;
    return due.isAfter(endOfNextWeek, 'day');
  });
  const todayCompleted = completionList.filter((tc: TaskCompletion) => {
    const done = getCompletionDoneMoment(tc);
    return done ? done.isSame(today, 'day') : false;
  });
  const thisWeekCompleted = completionList.filter((tc: TaskCompletion) => {
    const done = getCompletionDoneMoment(tc);
    return done ? done.isAfter(startOfWeek) && done.isBefore(today) : false;
  });
  const pastCompleted = completionList.filter((tc: TaskCompletion) => {
    const done = getCompletionDoneMoment(tc);
    return done ? done.isBefore(startOfWeek) : false;
  });

  // -- Calendar derived data --
  const calendarEvents = useMemo(() => {
    return filteredCompletions.map((tc: TaskCompletion) => ({
      date: toDay(getCompletionDueDate(tc)),
      data: tc,
    }));
  }, [filteredCompletions]);

  const filteredCalendarTasks = useMemo(() => {
    if (!selectedDates.length) return filteredCompletions;
    return filteredCompletions.filter((tc: TaskCompletion) => {
      const taskDate = toDay(getCompletionDueDate(tc));
      return selectedDates.some((d) => isSameDay(d, taskDate));
    });
  }, [filteredCompletions, selectedDates]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDates.length) return '';
    const sorted = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    const start = sorted[0];
    const end = sorted[sorted.length - 1];
    if (sorted.length === 1 || isSameDay(start, end)) return formatLongDate(start);
    return formatRangeLabel(start, end);
  }, [selectedDates]);

  const calendarOverdue = useMemo(
    () => filteredCompletions.filter((tc: TaskCompletion) => isCompletionOverdue(tc)),
    [filteredCompletions]
  );
  const calendarNonOverdue = useMemo(
    () => filteredCompletions.filter((tc: TaskCompletion) => !isCompletionOverdue(tc)),
    [filteredCompletions]
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAddTask = () => {
    setSelectedTaskDetail(null);
    setIsAdding(true);
  };

  const handleCloseDrawer = () => {
    setIsAdding(false);
    setSelectedTaskDetail(null);
  };

  const toggleSelectedDate = (date: Date) => {
    const day = toDay(date);
    const todayDate = toDay(new Date());
    setSelectedDates((prev) => {
      const wasSelected = prev.some((s) => isSameDay(s, day));
      if (wasSelected) {
        if (prev.length > 1) {
          setRangeAnchorDate(day);
          return [day];
        }
        const next = prev.filter((s) => !isSameDay(s, day));
        if (!next.length) {
          setRangeAnchorDate(todayDate);
          return [todayDate];
        }
        if (rangeAnchorDate && isSameDay(rangeAnchorDate, day)) setRangeAnchorDate(next[0]);
        return next;
      }
      if (!rangeAnchorDate || day < rangeAnchorDate) {
        setRangeAnchorDate(day);
        return [day];
      }
      if (isSameDay(rangeAnchorDate, day)) return prev;
      const newRange = getDateRange(rangeAnchorDate, day);
      if (newRange.length > 30) {
        setRangeAnchorDate(day);
        return [day];
      }
      return newRange;
    });
  };

  const selectDateRange = (dates: Date[]) => {
    setSelectedDates(dates.map(toDay));
    if (dates.length) setRangeAnchorDate(toDay(dates[dates.length - 1]));
  };

  const clearSelectedDates = () => {
    const todayDate = toDay(new Date());
    setSelectedDates([todayDate]);
    setRangeAnchorDate(todayDate);
    setCollapsedDateGroups({});
  };

  const handleCalendarShift = (dates: Date[]) => {
    if (dates.length > 1) {
      setVisibleDates((prev) => {
        const newStart = formatDay(dates[0]);
        const newEnd = formatDay(dates[dates.length - 1]);
        if (prev.length === 2 && formatDay(prev[0]) === newStart && formatDay(prev[1]) === newEnd) {
          return prev;
        }
        return [dates[0], dates[dates.length - 1]];
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Shared render function for a TaskCompletion row
  // ---------------------------------------------------------------------------

  const renderCompletion = (tc: TaskCompletion, index: number, inCompletedDrawer = false) => {
    const forecast = getCompletionForecastData(tc);
    const vehicleMax = forecast ? forecast.nextScheduledCarCount : 500;
    const vehicleCountValue = forecast ? forecast.totalCarCount : 0;
    const vehicleCountLabel = vehicleCountValue.toLocaleString('en-US');
    const nextLabel = forecast ? forecast.nextScheduledCarCount.toLocaleString('en-US') : '0';
    const isOverMax = vehicleCountValue > vehicleMax;

    const vehicleLabel = forecast ? (
      <span className="maintenance-item-time-right">
        <span
          className={`maintenance-item-time-value${isOverMax ? ' maintenance-item-time-over' : ''}`}
        >
          {vehicleCountLabel}
        </span>
        /{nextLabel} {intl.formatMessage({ id: 'vehicles', defaultMessage: 'vehicles' })}
      </span>
    ) : null;

    const timeLabel = forecast
      ? moment(forecast.forecastDate).format('MM/DD/YYYY')
      : getCompletionTimeLabel(tc, intl, timeZone);
    const overdue = isOverdueTime(tc, totalCarCount);
    const taskData = tc.task;
    const taskLocation = locations.find(
      (loc: any) => String(loc.type) === String(taskData?.locationId)
    );
    const siteData = sites.find((s: any) => String(s.id) === String(taskData?.siteId));
    const locationLabel = taskLocation?.message;
    const locationSiteLabel =
      locationLabel && siteData?.name ? `${locationLabel} - ${siteData.name}` : locationLabel;
    const timeEstimateLabel = '0 min';
    const peopleEstimateLabel = '1';

    const taskWithPriority =
      taskData && getPriorityLabel(taskData.priority)
        ? { ...taskData, priority: getPriorityLabel(taskData.priority) }
        : taskData;
    const taskCompletion = taskWithPriority ? { ...tc, task: taskWithPriority } : tc;

    return (
      <div className="maintenance-item" key={tc.id ?? `tc-${index}`}>
        <TaskListItem
          taskCompletion={taskCompletion}
          detailType={TaskListDetail.None}
          key={`maintenance-checklist-${tc.id ?? index}`}
          className="maintenance-task-item"
          showPriorityTag={false}
          displayOptionsMenu={false}
          displayMaintenanceColumns={viewTab === 'checklist'}
          displayTimeInfo={false}
          onOpen={() => {
            if (taskData && tc.taskId != null) {
              setSelectedTaskDetail({
                id: tc.taskId,
                label: taskData.name,
                subText: taskData.type,
              });
              dispatch(getTask(tc.taskId));
            }
          }}
          onToggleSnooze={fetchData}
          onToggleCompletion={
            viewTab === 'checklist'
              ? ({ taskCompletion: tcItem, completed, employeeId }: any) => {
                  if (!completed) {
                    if (tcItem.id) {
                      dispatch(
                        completeTaskCompletion({
                          employeeId,
                          taskIds: [tcItem.id],
                          notes: {},
                          callback: fetchData,
                        })
                      );
                    } else if (tcItem.taskId) {
                      dispatch(addTaskCompletion({ taskId: tcItem.taskId }, 'Maintenance'));
                    }
                  } else if (tcItem.id) {
                    dispatch(
                      revertTaskCompletion({
                        employeeId,
                        taskIds: [tcItem.id],
                        notes: null,
                        callback: fetchData,
                      })
                    );
                  }
                }
              : undefined
          }
          additionalLines={
            inCompletedDrawer ? (
              <div className="maintenance-item-time maintenance-item-time--stacked">
                <span>{timeLabel}</span>
                {locationSiteLabel && (
                  <div className="maintenance-item-location-row">
                    <span className="maintenance-item-location">{locationSiteLabel}</span>
                  </div>
                )}
                {vehicleLabel}
              </div>
            ) : (
              <div
                className={`maintenance-item-time maintenance-item-time--stacked${
                  overdue ? ' overdue' : ''
                }`}
              >
                <span className="maintenance-item-time-label">{timeLabel}</span>
                <div className="maintenance-item-location-row">
                  {locationSiteLabel && (
                    <span className="maintenance-item-location">{locationSiteLabel}</span>
                  )}
                </div>
              </div>
            )
          }
        />
        {!inCompletedDrawer &&
          tc.status !== 'Completed' &&
          (tc.task?.interval === toString(Interval, Interval.CarCount) ||
            tc.task?.interval === Interval.CarCount) && (
            <>
              {forecast && (
                <div className="maintenance-item-time maintenance-item-time--vehicle">
                  <span className="maintenance-item-time-right">
                    {vehicleCountLabel}/{nextLabel} vehicles
                  </span>
                </div>
              )}
              <ProgressBar
                value={forecast ? forecast.totalCarCount : 0}
                maxValue={forecast ? forecast.nextScheduledCarCount : 100}
                color={forecast ? forecast.color : undefined}
              />
            </>
          )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Filter configs
  // ---------------------------------------------------------------------------

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'nameQuery',
        type: 'search',
        name: intl.formatMessage({ id: 'search' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        searchTerm: filterBarValues.nameQuery,
        debounceMs: 0,
        onChange: (value: string | null) =>
          setFilterBarValues((prev) => ({ ...prev, nameQuery: value || '' })),
      },
      {
        id: 'locationFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'taskLocation', defaultMessage: 'Location' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        options: (locations || []).map((loc: any) => ({
          value: String(loc.type),
          text: loc.message,
          selected: (filterBarValues.locationFilter || []).map(String).includes(String(loc.type)),
        })),
        onChange: (values: string[]) =>
          setFilterBarValues((prev) => ({ ...prev, locationFilter: values })),
      },
      ...(viewTab === 'checklist'
        ? ([
            {
              id: 'dueDateRange',
              type: 'dateRange',
              name: intl.formatMessage({
                id: 'estimatedDueDate',
                defaultMessage: 'Estimated Due Date',
              }),
              placement: FilterPlacement.Chiclet,
              defaultVisible: true,
              native: true,
              startDate: filterBarValues.dueDateStart
                ? moment(filterBarValues.dueDateStart).toDate()
                : null,
              endDate: filterBarValues.dueDateEnd
                ? moment(filterBarValues.dueDateEnd).toDate()
                : null,
              onChange: ([startDate, endDate]: [Date | null, Date | null]) =>
                setFilterBarValues((prev) => ({
                  ...prev,
                  dueDateStart: startDate ? moment(startDate).toISOString() : '',
                  dueDateEnd: endDate ? moment(endDate).toISOString() : '',
                })),
            },
          ] as FilterConfig[])
        : []),
      {
        id: 'statusFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'taskStatus', defaultMessage: 'Status' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        options: Object.keys(TaskStatus).map((key) => ({
          value: key,
          text: key === 'Snoozed' ? 'Blocked' : key,
          selected: (filterBarValues.statusFilter || []).map(String).includes(key),
        })),
        onChange: (values: string[]) =>
          setFilterBarValues((prev) => ({ ...prev, statusFilter: values })),
      },
      {
        id: 'employeeFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'filterByTeamMember', defaultMessage: 'Team Member' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        options: (employees || []).map((e: any) => ({
          value: String(e.id),
          text: `${e.lastName}, ${e.firstName}`,
          selected: (filterBarValues.employeeFilter || []).map(String).includes(String(e.id)),
        })),
        onChange: (values: string[]) =>
          setFilterBarValues((prev) => ({ ...prev, employeeFilter: values })),
      },
      {
        id: 'intervalFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'interval', defaultMessage: 'Interval' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        options: Object.keys(Interval).map((key) => ({
          value: key,
          text: key === 'CarCount' ? 'Car Count' : key,
          selected: (filterBarValues.intervalFilter || []).map(String).includes(key),
        })),
        onChange: (values: string[]) =>
          setFilterBarValues((prev) => ({ ...prev, intervalFilter: values })),
      },
    ],
    [intl, locations, employees, filterBarValues, viewTab]
  );

  // ---------------------------------------------------------------------------
  // Calendar-specific sort helpers
  // ---------------------------------------------------------------------------

  const sortByName = (a: TaskCompletion, b: TaskCompletion) =>
    (a.task?.name ?? '').localeCompare(b.task?.name ?? '');

  const isDueToday = (tc: TaskCompletion) => {
    const todayMs = new Date().setHours(0, 0, 0, 0);
    const due = new Date(tc.scheduleEnd || tc.scheduleStart || '');
    return due.setHours(0, 0, 0, 0) <= todayMs;
  };

  const isTaskIncompleteToday = (tc: TaskCompletion) => tc.status !== 'Completed' && isDueToday(tc);
  const isTaskIncompleteLater = (tc: TaskCompletion) =>
    tc.status !== 'Completed' && !isDueToday(tc);
  const isTaskComplete = (tc: TaskCompletion) => tc.status === 'Completed';

  const sortComplete = (a: TaskCompletion, b: TaskCompletion) =>
    new Date(b.timestamp ?? '').getTime() - new Date(a.timestamp ?? '').getTime();

  const renderCalendarEvent = useMemo(() => {
    return (e: CalendarEvent) => {
      const tc = e.data;
      let statusColor = '';
      if (isCompletionOverdue(tc)) statusColor = ' overdue';
      if (tc?.status?.toLowerCase() == 'snoozed') statusColor = ' blocked';
      if (isTaskComplete(tc)) statusColor = ' done';
      return <span className={`task-event${statusColor}`} data-name={e.data.task?.name}></span>;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="maintenance-tasks-container">
      {/* Tabs + Employee selector */}
      <div className="tasks-filters">
        <div className="task-tabs">
          <button
            type="button"
            className={`button ${viewTab === 'checklist' ? 'active' : ''}`}
            onClick={() => setViewTab('checklist')}
          >
            <BsListCheck />
            <span className="desktop-label">
              {intl.formatMessage({ id: 'checklist', defaultMessage: 'Checklist' })}
            </span>
          </button>
          <button
            type="button"
            className={`button ${viewTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewTab('calendar')}
          >
            <BsCalendar2 />
            <span className="desktop-label">
              {intl.formatMessage({ id: 'calendar', defaultMessage: 'Calendar' })}
            </span>
          </button>
          <button
            type="button"
            className={`button ${viewTab === 'completed' ? 'active' : ''}`}
            onClick={() => setViewTab('completed')}
          >
            <IoMdCheckmarkCircleOutline />
            <span className="desktop-label">
              {intl.formatMessage({ id: 'completed', defaultMessage: 'Completed' })}
            </span>
          </button>
        </div>
        <div className="task-filter">
          <EmployeeDropdownAndAddButton onAddFunction={handleAddTask} page="maintenance" />
        </div>
      </div>

      {/* Filter bar */}
      <div className="maintenance-filter-bar">
        <PageFilters filters={filterConfigs} maxChicletFilters={4} />
      </div>

      {/* ============================================================ */}
      {/* CHECKLIST VIEW                                                */}
      {/* ============================================================ */}
      {viewTab === 'checklist' && (
        <div className="drawer-container">
          <div className="maintenace-list">
            {completionsLoading ? (
              <Loader />
            ) : (
              <>
                <div className="maintenance-details-header">
                  <span>{intl.formatMessage({ id: 'dueIn' })}</span>
                  <span>{intl.formatMessage({ id: 'interval' })}</span>
                  <span>{intl.formatMessage({ id: 'dueInDays' })}</span>
                </div>
                <div className="tasks-daily-table">
                  {overdueCompletions.length > 0 && (
                    <div className="maintenance-overdue-rollup">
                      <FilterSection
                        itemList={overdueCompletions}
                        renderFunction={renderCompletion}
                        title={intl.formatMessage({ id: 'overdue', defaultMessage: 'Overdue' })}
                        isOpen
                      />
                    </div>
                  )}
                  <FilterSection
                    itemList={todayCompletions}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'today', defaultMessage: 'Today' })}
                  />
                  <FilterSection
                    itemList={tomorrowCompletions}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'tomorrow', defaultMessage: 'Tomorrow' })}
                  />
                  <FilterSection
                    itemList={thisWeekCompletions}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'thisWeek', defaultMessage: 'This Week' })}
                    isOpen={false}
                  />
                  <FilterSection
                    itemList={nextWeekCompletions}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'nextWeek', defaultMessage: 'Next Week' })}
                    isOpen={false}
                  />
                  <FilterSection
                    itemList={laterCompletions}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'later', defaultMessage: 'Later' })}
                    isOpen={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* COMPLETED VIEW                                                */}
      {/* ============================================================ */}
      {viewTab === 'completed' && (
        <div className="drawer-container">
          <div className="maintenace-list">
            {completionsLoading ? (
              <Loader />
            ) : (
              <>
                <div className="tasks-daily-table">
                  <FilterSection
                    itemList={todayCompleted}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'today', defaultMessage: 'Today' })}
                  />
                  <FilterSection
                    itemList={thisWeekCompleted}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'thisWeek', defaultMessage: 'This Week' })}
                    isOpen={false}
                  />
                  <FilterSection
                    itemList={pastCompleted}
                    renderFunction={renderCompletion}
                    title={intl.formatMessage({ id: 'last30', defaultMessage: 'Last 30 Days' })}
                    isOpen={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* CALENDAR VIEW                                                 */}
      {/* ============================================================ */}
      {viewTab === 'calendar' && (
        <div className="maintenance-calendar-drawer-container">
          <DrawerContainer>
            <Drawer expanded={true} expandedWidth={66}>
              <Calendar
                events={calendarEvents}
                renderEvent={renderCalendarEvent}
                selectedDates={selectedDates}
                onSelectDate={toggleSelectedDate}
                onSelectRange={selectDateRange}
                initialView={defaultCalendarView}
                onViewShift={handleCalendarShift}
              />
            </Drawer>
            <div className="maintenace-list">
              <div
                className={`tasks-daily-table${
                  selectedDates.length > 0 ? ' tasks-daily-table--filtered' : ''
                }`}
              >
                {selectedDateLabel && (
                  <div className="tasks-date-range">
                    <span>{selectedDateLabel}</span>
                    {!(
                      selectedDates.length === 1 && isSameDay(selectedDates[0], toDay(new Date()))
                    ) && (
                      <button
                        className="tasks-date-range-clear"
                        type="button"
                        onClick={clearSelectedDates}
                        aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                      >
                        <i className="uil uil-times" />
                      </button>
                    )}
                  </div>
                )}
                {selectedDates.length > 0 ? (
                  selectedDates
                    .slice()
                    .filter((date) =>
                      filteredCalendarTasks.some((tc: TaskCompletion) =>
                        isSameDay(toDay(getCompletionDueDate(tc)), toDay(date))
                      )
                    )
                    .sort((a, b) => a.getTime() - b.getTime())
                    .map((date, iterator) => {
                      const tasksForDate = filteredCalendarTasks
                        .filter((tc: TaskCompletion) =>
                          isSameDay(toDay(getCompletionDueDate(tc)), toDay(date))
                        )
                        .sort(sortByName);
                      if (!tasksForDate.length) return null;
                      const label = date.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      });
                      const formattedLabel = formatLongDate(new Date(label));
                      const isCollapsed =
                        label in collapsedDateGroups
                          ? Boolean(collapsedDateGroups[label])
                          : iterator !== 0;
                      return (
                        <div className="tasks-date-group" key={label}>
                          <button
                            className="tasks-date-group-title"
                            type="button"
                            onClick={() =>
                              setCollapsedDateGroups((prev) => ({ ...prev, [label]: !isCollapsed }))
                            }
                          >
                            <span>{formattedLabel}</span>
                            <span className="tasks-date-group-count">{tasksForDate.length}</span>
                            <i
                              className={`uil uil-angle-${isCollapsed ? 'down' : 'up'}`}
                              aria-hidden="true"
                            />
                          </button>
                          {!isCollapsed &&
                            tasksForDate.map((tc: TaskCompletion, i: number) =>
                              renderCompletion(tc, i)
                            )}
                        </div>
                      );
                    })
                ) : (
                  <>
                    {calendarOverdue.length > 0 && (
                      <div className="maintenance-overdue-rollup">
                        <FilterSection
                          itemList={calendarOverdue}
                          renderFunction={renderCompletion}
                          title={intl.formatMessage({ id: 'overdue', defaultMessage: 'Overdue' })}
                          isOpen
                        />
                      </div>
                    )}
                    <FilterSection
                      itemList={calendarNonOverdue}
                      renderFunction={renderCompletion}
                      filterFunction={isTaskIncompleteToday}
                      sortFunction={sortByName}
                      title="Due Today"
                    />
                    <FilterSection
                      itemList={calendarNonOverdue}
                      renderFunction={renderCompletion}
                      filterFunction={isTaskIncompleteLater}
                      sortFunction={sortByName}
                      title="Due Later"
                      isOpen={false}
                    />
                    {completedList.length > 0 && (
                      <FilterSection
                        itemList={calendarNonOverdue}
                        renderFunction={renderCompletion}
                        filterFunction={isTaskComplete}
                        sortFunction={sortComplete}
                        title="Completed"
                        isOpen={false}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </DrawerContainer>
        </div>
      )}

      {/* Task detail overlay drawer */}
      <Drawer
        expanded={!!selectedTaskDetail || isAdding}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseDrawer}
      >
        {(selectedTaskDetail || isAdding) && (
          <div className="drawer-loading-container">
            {taskLoading && (
              <div className="drawer-loading-overlay">
                <Loader />
              </div>
            )}
            <TaskDetails
              taskId={selectedTaskDetail?.id ?? null}
              isNew={isAdding}
              type={TaskType.Maintenance}
              onClose={handleCloseDrawer}
              onSave={fetchData}
              onDelete={fetchData}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default MaintenanceTasks;
